import React from 'react';
import {
    Paper, Box, TextField, Typography, Stack, IconButton, Select,
    MenuItem, FormControl, InputLabel, Divider, Autocomplete, Chip, Button, Switch
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
// Se importarán los tipos desde la nueva página de edición que construiremos después
import { Path, PathItem, Offer, LandingPage, Campaign } from './CampaignEditorPage';

interface PathBuilderProps {
    pathData: Path;
    offers: Offer[];
    landingPages: LandingPage[];
    campaigns: Campaign[];
    onChange: (updatedPath: Path) => void;
    onDelete: () => void;
    dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
}

const PathBuilder: React.FC<PathBuilderProps> = ({ pathData, offers, landingPages, campaigns, onChange, onDelete, dragHandleProps }) => {

    const handleFieldChange = (field: keyof Path, value: any) => {
        onChange({ ...pathData, [field]: value });
    };

    const handleItemChange = (originalIndex: number, field: keyof PathItem, value: any) => {
        const newItems = [...(pathData.items || [])];
        const currentItem = newItems[originalIndex];
        
        if (field === 'item_type') {
            currentItem.item_id = undefined;
            currentItem.direct_url = '';
        }

        newItems[originalIndex] = { ...currentItem, [field]: value };
        handleFieldChange('items', newItems);
    };

    const addItem = (group: 'lander' | 'offer') => {
        let type: PathItem['item_type'] = 'offer';
        if (group === 'lander') {
            type = 'landing_page';
        }
        
        const newItems = [...(pathData.items || []), { item_type: type, item_id: undefined, weight: 100, direct_url: '', item_group: group }];
        handleFieldChange('items', newItems);
    };

    const removeItem = (originalIndex: number) => {
        const newItems = [...(pathData.items || [])];
        newItems.splice(originalIndex, 1);
        handleFieldChange('items', newItems);
    };

    const renderDestinationSelector = (item: PathItem, originalIndex: number) => {
        switch (item.item_type) {
            case 'landing_page':
            case 'offer':
            case 'campaign':
                let options: {id: number, name: string}[] = [];
                if (item.item_type === 'landing_page') options = landingPages;
                if (item.item_type === 'offer') options = offers;
                if (item.item_type === 'campaign') options = campaigns;
                
                return (
                    <Autocomplete
                        sx={{flex: 2}}
                        size="small"
                        options={options}
                        getOptionLabel={(option) => `(${option.id}) ${option.name}`}
                        value={options.find(o => o.id === item.item_id) || null}
                        onChange={(e, newValue) => handleItemChange(originalIndex, 'item_id', newValue?.id)}
                        renderInput={(params) => <TextField {...params} label="Seleccionar Destino" />}
                    />
                );
            case 'direct_url':
                return (
                    <TextField 
                        sx={{flex: 2}} 
                        size="small" 
                        label="URL Directa" 
                        value={item.direct_url || ''}
                        onChange={(e) => handleItemChange(originalIndex, 'direct_url', e.target.value)}
                    />
                );
            default:
                return null;
        }
    };
    
    return (
        <Paper elevation={3} sx={{ p: 2, borderLeft: 5, borderColor: 'primary.main' }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <IconButton {...dragHandleProps} sx={{ cursor: 'grab' }}><DragIndicatorIcon /></IconButton>
                <TextField
                    label="Nombre del Path"
                    value={pathData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    variant="standard"
                    fullWidth
                />
                <TextField 
                    label="Peso" 
                    type="number" 
                    value={pathData.weight} 
                    onChange={(e) => handleFieldChange('weight', parseInt(e.target.value, 10))} 
                    sx={{width: '100px'}} 
                    variant="standard"
                />
                <Switch defaultChecked />
                <IconButton onClick={onDelete} color="error"><DeleteIcon /></IconButton>
            </Stack>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>LANDING PAGES</Typography>
            <Stack spacing={1.5} mb={2}>
                {(pathData.items || []).map((item, index) => item.item_group === 'lander' && (
                     <Stack direction="row" spacing={1} key={index} alignItems="center">
                        <Chip label={item.item_type === 'landing_page' ? 'Lander' : 'Directo'} size="small" />
                        {renderDestinationSelector(item, index)}
                        <TextField type="number" label="Peso" value={item.weight} onChange={(e) => handleItemChange(index, 'weight', parseInt(e.target.value, 10))} sx={{width: '100px'}} size="small"/>
                        <IconButton onClick={() => removeItem(index)} size="small"><DeleteIcon /></IconButton>
                    </Stack>
                ))}
                <Stack direction="row" spacing={1}>
                    <Button onClick={() => addItem('lander')} size="small" variant="outlined">Lander +</Button>
                    <Button onClick={() => addItem('lander')} size="small" variant="outlined">Directo +</Button>
                </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>OFFERS</Typography>
            <Stack spacing={1.5}>
                {(pathData.items || []).map((item, index) => item.item_group === 'offer' && (
                     <Stack direction="row" spacing={1} key={index} alignItems="center">
                        <FormControl fullWidth size="small" sx={{flex: 1}}>
                            <InputLabel>Tipo</InputLabel>
                            <Select label="Tipo" value={item.item_type} onChange={(e) => handleItemChange(index, 'item_type', e.target.value as any)}>
                                <MenuItem value="offer">Oferta</MenuItem>
                                <MenuItem value="campaign">Campaña</MenuItem>
                                <MenuItem value="direct_url">URL Directa</MenuItem>
                            </Select>
                        </FormControl>
                        {renderDestinationSelector(item, index)}
                        <TextField type="number" label="Peso" value={item.weight} onChange={(e) => handleItemChange(index, 'weight', parseInt(e.target.value, 10))} sx={{width: '100px'}} size="small"/>
                        <IconButton onClick={() => removeItem(index)} size="small"><DeleteIcon /></IconButton>
                    </Stack>
                ))}
            </Stack>
            <Button onClick={() => addItem('offer')} size="small" startIcon={<AddCircleIcon />}>Añadir Oferta/Destino</Button>
        </Paper>
    );
};

export default PathBuilder;
